/**
 * Placeholder utilities for AI rewrite flows.
 *
 * Both the RTE (Tiptap/HTML) and Feed (Slate/markdown) paths need to protect
 * certain tokens — links, images, user mentions — before sending text to the
 * AI, and restore them afterwards. This file provides both sets of helpers.
 *
 * ## RTE (HTML) — `extractLinkAndImg` / `restorePlaceholders`
 * Replaces `<a>` and `<img>` elements with `[PEGA-IMG-XXXX]` / `[PEGA-LINK-XXXX]`
 * placeholders using DOMParser. Reduces payload size and prevents the AI from
 * stripping or mangling anchor/image tags. Images wrapped in an anchor are
 * captured as a single LINK placeholder so the full `<a><img/></a>` structure
 * is stored intact.
 *
 * ## Feed (markdown) — `extractMentions` / `restoreMentionPlaceholders`
 * Replaces `<pega-mention .../>` XML elements with `[PEGA-MENTION-XXXX]`
 * placeholders using regex. Prevents the AI from dropping or mangling user
 * @mention markup that is stored inline in Slate markdown.
 *
 * In both cases: placeholders the AI dropped are appended at the end;
 * hallucinated placeholder strings not in the map are stripped;
 * placeholder-like strings the user literally typed are preserved.
 */

import { createUID } from '@pega/cosmos-react-core';

export interface PlaceholderMap {
  [placeholder: string]: string;
}

interface ExtractResult {
  placeholderHtml: string;
  placeholderMap: PlaceholderMap;
  userPlaceholders: string[];
}

const PLACEHOLDER_PATTERN = /\[PEGA-(?:IMG|LINK)-[^\]]+\]/g;

function createUniquePlaceholder(
  prefix: string,
  existingMap: PlaceholderMap,
  html: string
): string {
  let placeholder: string;
  do {
    placeholder = `[PEGA-${prefix}-${createUID()}]`;
  } while (placeholder in existingMap || html.includes(placeholder));
  return placeholder;
}

/**
 * Replaces all `<a>` and standalone `<img>` elements in the HTML with unique
 * placeholders. Uses DOMParser to reliably locate elements rather than regex.
 * Anchors are processed first so that linked images (`<a><img/></a>`) are
 * captured as a single LINK placeholder.
 */
export function extractLinkAndImg(html: string): ExtractResult {
  const placeholderMap: PlaceholderMap = {};
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Record placeholder-like strings the user literally typed so they are
  // not mistakenly stripped during restoration (they are NOT hallucinations).
  const userPlaceholders = (html.match(PLACEHOLDER_PATTERN) ?? []).filter(
    (match, i, arr) => arr.indexOf(match) === i
  );

  // Process anchor tags first — captures linked images as one unit
  for (const anchor of Array.from(doc.body.querySelectorAll('a'))) {
    const placeholder = createUniquePlaceholder('LINK', placeholderMap, html);
    placeholderMap[placeholder] = anchor.outerHTML;
    anchor.replaceWith(doc.createTextNode(placeholder));
  }

  // Process remaining standalone img tags (not inside an <a> already replaced)
  for (const img of Array.from(doc.body.querySelectorAll('img'))) {
    const placeholder = createUniquePlaceholder('IMG', placeholderMap, html);
    placeholderMap[placeholder] = img.outerHTML;
    img.replaceWith(doc.createTextNode(placeholder));
  }

  return { placeholderHtml: doc.body.innerHTML, placeholderMap, userPlaceholders };
}

/**
 * Restores placeholders in the rewritten HTML with the original HTML elements.
 * Placeholders that were dropped by the AI are appended at the end, each
 * wrapped in a `<p>` tag (TipTap's default block element).
 * Any hallucinated placeholder strings (not in the map) are stripped.
 */
export function restorePlaceholders(
  html: string,
  placeholderMap: PlaceholderMap,
  userPlaceholders: string[] = []
): string {
  let result = html;
  const missingElements: string[] = [];

  for (const [placeholder, originalHtml] of Object.entries(placeholderMap)) {
    if (result.includes(placeholder)) {
      result = result.replaceAll(placeholder, originalHtml);
    } else {
      missingElements.push(originalHtml);
    }
  }

  // Strip any hallucinated placeholder strings the AI may have invented,
  // but preserve placeholder-like strings the user literally typed.
  const userSet = new Set(userPlaceholders);
  result = result.replace(PLACEHOLDER_PATTERN, match => (userSet.has(match) ? match : ''));

  if (missingElements.length > 0) {
    result = `${result}${missingElements.map(el => `<p>${el}</p>`).join('')}`;
  }

  return result;
}

// ---------------------------------------------------------------------------
// Feed (markdown) — pega-mention placeholder helpers
// ---------------------------------------------------------------------------

/** Regex matching a <pega-mention .../> self-closing XML element inline in markdown. */
const PEGA_MENTION_REGEX = /<pega-mention (?:[^\n/]|\/(?!>))+\/>/g;

/** Matches [PEGA-MENTION-XXXX] bracket placeholders injected by extractMentions. */
const MENTION_PLACEHOLDER_PATTERN = /\[PEGA-MENTION-[^\]]+\]/g;

interface ExtractMentionsResult {
  cleanText: string;
  mentionMap: PlaceholderMap;
  userPlaceholders: string[];
}

function createUniqueMentionPlaceholder(existingMap: PlaceholderMap, text: string): string {
  let placeholder: string;
  do {
    placeholder = `[PEGA-MENTION-${createUID()}]`;
  } while (placeholder in existingMap || text.includes(placeholder));
  return placeholder;
}

/**
 * Extracts all `<pega-mention .../>` elements from a markdown string,
 * replacing them with unique `[PEGA-MENTION-XXXX]` placeholders.
 *
 * Call this before sending Feed text to the AI for rewriting. After the AI
 * responds, call `restoreMentionPlaceholders` to put the original mention XML back.
 */
export function extractMentions(markdown: string): ExtractMentionsResult {
  const mentionMap: PlaceholderMap = {};

  // Record placeholder-like strings the user literally typed so they are
  // not mistakenly stripped during restoration.
  const userPlaceholders = (markdown.match(MENTION_PLACEHOLDER_PATTERN) ?? []).filter(
    (match, i, arr) => arr.indexOf(match) === i
  );

  const cleanText = markdown.replace(PEGA_MENTION_REGEX, match => {
    const placeholder = createUniqueMentionPlaceholder(mentionMap, markdown);
    mentionMap[placeholder] = match;
    return placeholder;
  });

  return { cleanText, mentionMap, userPlaceholders };
}

/**
 * Restores `<pega-mention .../>` XML elements in AI-rewritten Feed text.
 *
 * - Placeholders present in the AI response are replaced with the original mention XML.
 * - Placeholders dropped by the AI are appended at the end (preserving all mentions).
 * - Hallucinated placeholder strings (not in the map) are stripped, unless they were
 *   typed literally by the user.
 */
export function restoreMentionPlaceholders(
  text: string,
  mentionMap: PlaceholderMap,
  userPlaceholders: string[] = []
): string {
  let result = text;
  const missingMentions: string[] = [];

  for (const [placeholder, originalXml] of Object.entries(mentionMap)) {
    if (result.includes(placeholder)) {
      result = result.replaceAll(placeholder, originalXml);
    } else {
      missingMentions.push(originalXml);
    }
  }

  const userSet = new Set(userPlaceholders);
  result = result.replace(MENTION_PLACEHOLDER_PATTERN, match => (userSet.has(match) ? match : ''));

  if (missingMentions.length > 0) {
    const appendedMentions = missingMentions.join(' ');

    if (!result) {
      result = appendedMentions;
    } else if (/\s$/.test(result)) {
      result = `${result}${appendedMentions}`;
    } else {
      result = `${result} ${appendedMentions}`;
    }
  }

  return result;
}
